// 환율 관련 유틸리티 함수

/**
 * KRW를 USD로 변환
 * @param krwAmount KRW 금액
 * @param exchangeRate 환율 (KRW/USD)
 * @returns USD 금액
 */
export const convertKRWtoUSD = (krwAmount: number, exchangeRate: number): number => {
  if (exchangeRate === 0) return 0;
  return krwAmount / exchangeRate;
};

/**
 * USD를 KRW로 변환
 * @param usdAmount USD 금액
 * @param exchangeRate 환율 (KRW/USD)
 * @returns KRW 금액
 */
export const convertUSDtoKRW = (usdAmount: number, exchangeRate: number): number => {
  return usdAmount * exchangeRate;
};

/**
 * 가격을 현재 언어에 맞게 포맷팅
 * @param amount 금액
 * @param currency 통화 ('KRW' | 'USD')
 * @param lang 언어 ('ko' | 'en')
 * @returns 포맷된 가격 문자열
 */
export const formatPrice = (amount: number, currency: 'KRW' | 'USD', lang: 'ko' | 'en'): string => {
  if (currency === 'USD') {
    // USD는 소수점 2자리까지 표시
    const formattedAmount = amount.toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `USD ${formattedAmount}`;
  } else {
    // KRW는 정수로 표시
    const formattedAmount = Math.floor(amount).toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US');
    return `KRW ${formattedAmount}`;
  }
};

/**
 * 가격을 현재 언어에 맞는 통화로 표시
 * @param krwAmount KRW 금액
 * @param exchangeRate 환율
 * @param lang 언어
 * @returns 포맷된 가격 문자열
 */
export const formatPriceByLanguage = (
  krwAmount: number, 
  exchangeRate: number, 
  lang: 'ko' | 'en'
): string => {
  if (lang === 'ko') {
    // 한국어: KRW만 표시
    return formatPrice(krwAmount, 'KRW', lang);
  } else {
    // 영어: USD만 표시
    const usdAmount = convertKRWtoUSD(krwAmount, exchangeRate);
    return formatPrice(usdAmount, 'USD', lang);
  }
};

/**
 * 간단한 가격 포맷 (통화 기호 분리) - 언어별 단일 통화
 * @param krwAmount KRW 금액
 * @param exchangeRate 환율
 * @param lang 언어
 * @returns 통화와 금액이 분리된 객체
 */
export const formatPriceComponentsByLanguage = (
  krwAmount: number, 
  exchangeRate: number, 
  lang: 'ko' | 'en'
): { currency: string; amount: string } => {
  if (lang === 'ko') {
    // 한국어: KRW만 표시
    return {
      currency: 'KRW',
      amount: Math.floor(krwAmount).toLocaleString('ko-KR')
    };
  } else {
    // 영어: USD만 표시
    const usdAmount = convertKRWtoUSD(krwAmount, exchangeRate);
    return {
      currency: 'USD',
      amount: usdAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    };
  }
};
